import {
  type FC,
  type PropsWithChildren,
  type SyntheticEvent,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react'
import { Modal, type ModalProps, useIsMobileVariant } from '@dhl-ui/components'
import { useNativeDialogBackdrop } from './useNativeBackdrop.js'
import { type NativeModalContextProps, NativeModalProvider } from './NativeModalProvider.js'

export type NativeModalProps = Omit<ModalProps, 'children'> &
  PropsWithChildren<{
    onOpen?: () => void
  }>

export const NativeModal: FC<NativeModalProps> = ({
  children,
  isFullscreen,
  isSwipeable,
  onClose,
  onOpen,
  open,
  swipeCloseMode = 'handle',
  ...rest
}) => {
  const { handleDialogClosing, handleDialogOpen } = useNativeDialogBackdrop()
  const isMobileVariant = useIsMobileVariant()

  const handleClose = useCallback(
    (event: SyntheticEvent, reason?: 'backdropClick' | 'escapeKeyDown') => {
      handleDialogClosing()
      onClose?.(event, reason)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onClose]
  )

  const contextValue: NativeModalContextProps = useMemo(() => ({ onClose: handleClose }), [handleClose])

  const previousOpen = useRef(false)

  useLayoutEffect(() => {
    if (previousOpen.current === open) {
      return
    }
    previousOpen.current = open

    if (open) {
      handleDialogOpen()
      onOpen?.()
    } else {
      handleDialogClosing()
    }
  }, [handleDialogClosing, handleDialogOpen, onOpen, open])

  return (
    <NativeModalProvider value={contextValue}>
      <Modal
        {...rest}
        disableBackdropTransition
        isCloseable={!!onClose}
        isFullscreen={isFullscreen ?? isMobileVariant}
        isSwipeable={isSwipeable ?? isMobileVariant}
        open={open}
        swipeCloseMode={swipeCloseMode}
        onClose={handleClose}
      >
        {children}
      </Modal>
    </NativeModalProvider>
  )
}
